(function () {
  if (customElements.get('five-five-six-tripwire-offer')) return;

  const TEN_ROUND_MAG_STATES = new Set(['CA', 'NY', 'NJ', 'CT', 'MA', 'HI', 'MD', 'CO', 'WA', 'IL', 'VT']);

  customElements.define('five-five-six-tripwire-offer', class FiveFiveSixTripwireOffer extends HTMLElement {
    constructor() {
      super();
      this.state = {
        busy: false,
        plan: null,
        can: null,
        shipState: '',
        insurance: null
      };
      this.tripwireVariantId = this.toPositiveInt(this.dataset.tripwireVariantId);
      this.tripwirePriceCents = this.toPositiveInt(this.dataset.tripwirePriceCents, 4500);
      this.sellingPlanId = this.toPositiveInt(this.dataset.sellingPlanId);
      this.offerKey = this.dataset.offerKey || '556_100_for_45';
      this.clubOfferKey = this.dataset.clubOfferKey || '556_45_tripwire_club';
      this.starterRounds = this.toPositiveInt(this.dataset.starterRounds, 100);
      this.starterOfferLabel = this.dataset.starterOfferLabel || `${this.starterRounds} rounds for ${this.formatMoney(this.tripwirePriceCents)}`;
      this.redirectUrl = this.dataset.redirectUrl || '/checkout';
      // Page mode (Oct 29): with data-next-url the starter's button adds the starter, then opens the monthly offer as
      // its own page instead of the pop-up. Pages without it keep the pop-up.
      this.nextUrl = this.dataset.nextUrl || '';
      this.limitMessage = this.dataset.limitMessage || 'This special offer is limited to one 100-round package per order.';
      this.requireReviewCompletion = this.dataset.requireReviewCompletion === 'true';
    }

    connectedCallback() {
      this.builder = this.querySelector('[data-builder]');
      this.bindBuilder();
      this.bindChoices();
      this.bindShippingState();
      this.bindActions();
      this.bindFaq();
      this.initStickyBar();
      this.syncInitialState();
      this.updateReview();
      this.track('offer_view');
    }

    bindBuilder() {
      this.querySelectorAll('[data-open-builder]').forEach((button) => {
        button.addEventListener('click', () => this.prepareBuilder(button));
      });
      this.querySelectorAll('[data-close-builder]').forEach((button) => {
        button.addEventListener('click', () => this.closeBuilder());
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.builder && !this.builder.hidden && !this.requireReviewCompletion) this.closeBuilder();
      });
    }

    async prepareBuilder(trigger) {
      if ((!this.builder && !this.nextUrl) || this.state.busy) return;
      if (!this.tripwireVariantId) {
        this.setCartConfirmation(false, `The ${this.starterRounds}-round offer is temporarily unavailable.`);
        this.openBuilder(trigger);
        this.showError(`The ${this.starterRounds}-round offer is temporarily unavailable. Please try again shortly.`);
        return;
      }

      this.state.busy = true;
      this.showError('');
      let leaving = false;
      const originalLabel = trigger?.innerHTML;
      if (trigger) {
        trigger.classList.add('is-busy');
        trigger.setAttribute('aria-busy', 'true');
        trigger.textContent = 'Adding your ammo…';
      }

      try {
        let cart = await this.getCart();
        if (await this.keepOneTripwire(this.findTripwireLines(cart.items || []))) cart = await this.getCart();
        const existingTripwireLines = this.findTripwireLines(cart.items || []);

        const needsTripwire = existingTripwireLines.length === 0;
        const insuranceChange = await this.getInsuranceChange(
          false,
          null,
          needsTripwire ? this.tripwirePriceCents : 0
        );
        const items = [];
        if (needsTripwire) items.push(this.buildTripwireItem());
        if (insuranceChange?.item) items.push(insuranceChange.item);

        const addedItems = items.length > 0 ? await this.addItems(items) : [];
        try {
          if (insuranceChange?.removeKeys?.length) {
            await this.removeReplacedInsurance(insuranceChange.item);
          }
          cart = await this.getCart();
          this.verifyInsurance(cart, false);
        } catch (error) {
          await this.removeAddedLines(addedItems);
          throw error;
        }

        this.syncInsuranceStateFromCart(cart);
        if (needsTripwire) {
          this.track('tripwire_added_before_builder', {value: this.tripwirePriceCents / 100});
        }

        this.setCartConfirmation(true, `${this.starterRounds} rounds of PMC X-TAC 5.56`);
        if (this.nextUrl) {
          leaving = true;
          window.location.assign(this.nextUrl);
          return;
        }
        this.setBusy(false);
        this.openBuilder(trigger);
      } catch (error) {
        console.error('[5.56 Starter] Could not prepare offer builder:', error);
        // the reason goes in the header: the [data-error-message] line sits below the fold on phones
        this.setCartConfirmation(false, error.message || 'We could not add your ammo to the cart.');
        this.setBusy(false);
        this.openBuilder(trigger);
        this.showError(error.message || 'We could not add your ammo. Please try again.');
      } finally {
        // leaving for the next page: keep "Adding your ammo…" on the button until it loads
        if (trigger && !leaving) {
          trigger.classList.remove('is-busy');
          trigger.removeAttribute('aria-busy');
          if (originalLabel !== undefined) trigger.innerHTML = originalLabel;
        }
      }
    }

    bindChoices() {
      this.addEventListener('click', (event) => {
        const plan = event.target.closest('[data-plan-option]');
        if (plan && this.contains(plan) && !plan.disabled && !this.state.busy) {
          this.selectPlan(plan);
          return;
        }

        const can = event.target.closest('[data-can-option]');
        if (can && this.contains(can) && !can.disabled && !this.state.busy) {
          this.selectCan(can);
        }
      });

      this.querySelectorAll('[data-plan-option], [data-can-option]').forEach((option) => {
        option.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          option.click();
        });
      });
    }

    bindShippingState() {
      this.querySelector('[data-gift-state]')?.addEventListener('change', (event) => {
        this.state.shipState = event.target.value || '';
        if (this.state.shipState) this.clearStateError();
        const selectedGift = this.querySelector('[data-can-option].is-selected:not(:disabled)');
        if (selectedGift) this.readCan(selectedGift);
        this.updateReview();
        this.track('gift_state_select', {ship_state: this.state.shipState || null});
      });
    }

    bindActions() {
      this.querySelector('[data-add-combined]')?.addEventListener('click', () => {
        this.addOffer({includeSubscription: true});
      });
      this.querySelector('[data-add-tripwire-only]')?.addEventListener('click', () => {
        this.addOffer({includeSubscription: false});
      });
    }

    bindFaq() {
      this.querySelectorAll('.nmt-faq details').forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          this.querySelectorAll('.nmt-faq details').forEach((other) => {
            if (other !== item) other.removeAttribute('open');
          });
        });
      });
    }

    initStickyBar() {
      const sticky = this.querySelector('[data-sticky-bar]');
      const heroButton = this.querySelector('.nmt-button--hero');
      if (!sticky || !heroButton || !('IntersectionObserver' in window)) return;

      this.stickyObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.bottom < 0);
      }, {threshold: 0});
      this.stickyObserver.observe(heroButton);
    }

    syncInitialState() {
      const plan = this.querySelector('[data-plan-option].is-selected:not(:disabled)') ||
        this.querySelector('[data-plan-option]:not(:disabled)');
      const can = this.querySelector('[data-can-option].is-selected:not(:disabled)') ||
        this.querySelector('[data-can-option]:not(:disabled)');
      if (plan) this.readPlan(plan);
      if (can) this.readCan(can);
      this.paintSelection('[data-plan-option]', plan);
      this.paintSelection('[data-can-option]', can);
    }

    openBuilder(trigger) {
      if (!this.builder || this.state.busy) return;
      this.lastFocusedElement = trigger || document.activeElement;
      this.builder.hidden = false;
      document.documentElement.classList.add('nmt-lock-scroll');
      this.querySelector('.nmt-builder__dialog')?.focus({preventScroll: true});
      this.track('builder_open');
    }

    closeBuilder() {
      if (!this.builder || this.builder.hidden || this.state.busy || this.requireReviewCompletion) return;
      this.builder.hidden = true;
      document.documentElement.classList.remove('nmt-lock-scroll');
      this.lastFocusedElement?.focus?.({preventScroll: true});
      this.track('builder_close');
    }

    selectPlan(option) {
      this.readPlan(option);
      this.paintSelection('[data-plan-option]', option);
      this.updateReview();
      this.track('plan_select', {rounds: this.state.plan.rounds, price: this.state.plan.priceCents / 100});
    }

    selectCan(option) {
      this.readCan(option);
      if (!this.state.can.needsState) this.clearStateError();
      this.paintSelection('[data-can-option]', option);
      this.updateReview();
      this.track('gift_select', {gift: this.state.can.title, gift_type: this.state.can.type});
    }

    readPlan(option) {
      this.state.plan = {
        variantId: this.toPositiveInt(option.dataset.variantId),
        sku: option.dataset.sku || '',
        rounds: this.toPositiveInt(option.dataset.rounds),
        targets: this.toPositiveInt(option.dataset.targets),
        firstShipmentRounds: this.toPositiveInt(option.dataset.firstShipmentRounds),
        priceCents: this.toPositiveInt(option.dataset.priceCents),
        priceLabel: option.dataset.priceLabel || ''
      };
    }

    readCan(option) {
      const giftType = option.dataset.giftType || 'engraved_ammo_can';
      const standardVariantId = this.toPositiveInt(option.dataset.standardVariantId || option.dataset.variantId);
      const compliantVariantId = this.toPositiveInt(option.dataset.compliantVariantId || option.dataset.variantId);
      const needsState = giftType === 'engraved_pmag';
      const useCompliant = needsState && TEN_ROUND_MAG_STATES.has(this.state.shipState);
      this.state.can = {
        variantId: useCompliant ? compliantVariantId : standardVariantId,
        standardVariantId,
        compliantVariantId,
        type: giftType,
        needsState,
        capacity: needsState && this.state.shipState ? (useCompliant ? '10-round' : '30-round') : '',
        title: option.dataset.title || 'Selected first-order gift',
        image: option.dataset.image || ''
      };
    }

    paintSelection(selector, selected) {
      this.querySelectorAll(selector).forEach((option) => {
        const active = option === selected && !option.disabled;
        option.classList.toggle('is-selected', active);
        option.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }

    updateReview() {
      const plan = this.state.plan;
      const can = this.state.can;
      if (!plan) return;

      const combinedTotal = this.tripwirePriceCents + plan.priceCents;
      const targetCount = plan.targets || Math.round(plan.rounds / 12);
      this.setText('[data-review-plan]', `${plan.rounds}-round Carbine Club package`);
      this.setText('[data-review-plan-price]', plan.priceLabel || this.formatMoney(plan.priceCents));
      const giftReview = can?.needsState && can.capacity
        ? `${can.title} — ${can.capacity} for ${this.getStateLabel() || 'this state'}`
        : can?.title;
      this.setText('[data-review-can]', giftReview || 'Selected first-order gift');
      this.setText('[data-review-total]', this.formatMoney(combinedTotal));
      const combinedLabel = this.dataset.combinedLabel;
      this.setText('[data-combined-action]', combinedLabel
        ? combinedLabel.replace('{price}', plan.priceLabel || this.formatMoney(plan.priceCents)).replace('{rounds}', plan.rounds)
        : `Yes — Add My ${plan.rounds}-Round Delivery`);
      this.setText('[data-review-renewal-rounds]', `${plan.rounds} rounds`);
      this.setText('[data-review-renewal-targets]', `${targetCount} ${this.dataset.targetNoun || 'reactive targets'}`);
      this.setText('[data-review-renewal-price]', plan.priceLabel || this.formatMoney(plan.priceCents));
      const renewalSummary = this.dataset.renewalSummary;
      this.setText('[data-review-renewal-summary]', renewalSummary
        ? renewalSummary.replace('{starter}', this.formatMoney(this.tripwirePriceCents).replace(/\.00$/, ''))
        : `The ${this.formatMoney(this.tripwirePriceCents)} starter and engraved gift do not repeat. Skip, pause, change, or cancel before the next charge.`);

      const combinedButton = this.querySelector('[data-add-combined]');
      const stateWrap = this.querySelector('[data-gift-state-wrap]');
      if (stateWrap) stateWrap.hidden = !can?.needsState;
      this.setText('[data-gift-capacity-note]', can?.needsState && can.capacity
        ? `${can.capacity} engraved PMAG automatically selected for ${this.getStateLabel() || 'this state'}.`
        : 'Choose your ship-to state. We’ll automatically select the appropriate 10- or 30-round engraved PMAG.');
      if (combinedButton) {
        const giftReady = Boolean(can?.variantId);
        const ready = Boolean(this.tripwireVariantId && this.sellingPlanId && plan.variantId && giftReady);
        combinedButton.disabled = this.state.busy || !ready;
        combinedButton.setAttribute('aria-disabled', combinedButton.disabled ? 'true' : 'false');
      }
    }

    async addOffer({includeSubscription}) {
      if (this.state.busy) return;
      if (!this.tripwireVariantId) {
        this.showError(`The ${this.starterRounds}-round offer is temporarily unavailable. Please try again shortly.`);
        return;
      }
      if (includeSubscription && (!this.state.plan?.variantId || !this.sellingPlanId || !this.state.can?.variantId)) {
        this.showError('Choose your delivery and first-order gift.');
        return;
      }
      if (includeSubscription && this.state.can.needsState && !this.state.shipState) {
        this.showStateError();
        return;
      }

      this.setBusy(true);
      this.showError('');

      try {
        let cart = await this.getCart();
        if (await this.keepOneTripwire(this.findTripwireLines(cart.items || []))) cart = await this.getCart();
        const existingTripwireLines = this.findTripwireLines(cart.items || []);
        const existingClubLines = this.findClubLines(cart.items || []);
        const existingGiftLines = this.findGiftLines(cart.items || []);

        if (includeSubscription) {
          await this.removeLegacyClubDisplayProperties(existingClubLines);
        }

        const items = [];
        const needsTripwire = existingTripwireLines.length === 0;
        const needsClub = includeSubscription && existingClubLines.length === 0;
        let insuranceChange = null;
        if (needsTripwire) items.push(this.buildTripwireItem());

        if (includeSubscription) {
          if (needsClub) items.push(this.buildSubscriptionItem());
          if (existingGiftLines.length === 0) items.push(this.buildCanItem());

          const merchandiseSubtotalDelta =
            (needsTripwire ? this.tripwirePriceCents : 0) +
            (needsClub ? this.state.plan.priceCents : 0);
          insuranceChange = await this.getSubscriptionInsuranceChange(merchandiseSubtotalDelta);
          if (insuranceChange?.item) items.push(insuranceChange.item);
        } else {
          insuranceChange = await this.getInsuranceChange(
            false,
            null,
            needsTripwire ? this.tripwirePriceCents : 0
          );
          if (insuranceChange?.item) items.push(insuranceChange.item);
        }

        const addedItems = items.length > 0 ? await this.addItems(items) : [];
        try {
          if (includeSubscription) await this.verifyGiftDiscount(addedItems);
          if (insuranceChange?.removeKeys?.length) {
            await this.removeReplacedInsurance(insuranceChange.item);
          }
          const finalCart = await this.getCart();
          this.verifyInsurance(finalCart, includeSubscription);
        } catch (error) {
          await this.removeAddedLines(addedItems);
          throw error;
        }

        this.track(includeSubscription ? 'combined_add_to_cart' : 'tripwire_add_to_cart', {
          plan_rounds: includeSubscription ? this.state.plan.rounds : null,
          first_shipment_rounds: includeSubscription ? this.state.plan.firstShipmentRounds : this.starterRounds,
          value: includeSubscription
            ? (this.tripwirePriceCents + this.state.plan.priceCents) / 100
            : this.tripwirePriceCents / 100
        });
        window.location.assign(this.redirectUrl || '/checkout');
      } catch (error) {
        console.error('[5.56 Starter] Add to cart failed:', error);
        this.showError(error.message || 'We could not add this offer. Please try again.');
        this.setBusy(false);
      }
    }

    buildTripwireItem() {
      return {
        id: this.tripwireVariantId,
        quantity: 1,
        properties: {
          _offer_key: this.offerKey,
          _tripwire_item: 'true',
          _tripwire_limit: '1',
          'Offer': this.starterOfferLabel
        }
      };
    }

    buildSubscriptionItem() {
      const plan = this.state.plan;
      return {
        id: plan.variantId,
        quantity: 1,
        selling_plan: this.sellingPlanId,
        properties: {
          _offer_key: this.offerKey,
          _subscription_offer: this.clubOfferKey,
          _starter_variant: String(this.tripwireVariantId),
          _selected_gift: this.state.can.title,
          _first_shipment_rounds: String(plan.firstShipmentRounds || plan.rounds + this.starterRounds)
        }
      };
    }

    buildCanItem() {
      return {
        id: this.state.can.variantId,
        quantity: 1,
        properties: {
          _offer_key: this.offerKey,
          _subscription_offer: this.clubOfferKey,
          _gift_item: 'true',
          _gift_type: this.state.can.type,
          _first_shipment_only: 'true',
          _gift_for_variant: String(this.state.plan.variantId),
          _gift_for_subscription: String(this.sellingPlanId),
          'Free first-order gift': this.state.can.title,
          ...(this.state.can.needsState ? {
            'PMAG Capacity': this.state.can.capacity,
            'PMAG Shipping State': this.state.shipState
          } : {})
        }
      };
    }

    async getSubscriptionInsuranceChange(merchandiseSubtotalDelta) {
      return this.getInsuranceChange(true, 30, merchandiseSubtotalDelta);
    }

    async getInsuranceChange(isSubscription, frequency, merchandiseSubtotalDelta) {
      const core = window.SubscriptionInsuranceCore;
      if (!core) return null;

      try {
        const action = await core.determineInsuranceAction(isSubscription, frequency, {
          merchandiseSubtotalDelta
        });
        if (!action || action.action === 'none') {
          return {
            item: null,
            removeKeys: [],
            action: action || {action: 'none'}
          };
        }
        // $1,000+ carts: premium protection is opt-in, so the core asks to drop a lower tier rather than add one.
        // removeReplacedInsurance(null) removes every insurance line.
        if (action.action === 'remove') {
          return {
            item: null,
            removeKeys: (action.removeKeys || []).filter(Boolean),
            action
          };
        }

        const insuranceItem = core.buildInsuranceItem(
          action.addType,
          action.frequency,
          action.variantId
        );

        if (!insuranceItem) {
          throw new Error('We could not finish preparing your order. Please try again.');
        }

        if (
          action.addType === 'subscription' &&
          !insuranceItem.selling_plan
        ) {
          throw new Error('We could not finish preparing your order. Please try again.');
        }

        return {
          item: insuranceItem,
          removeKeys: action.action === 'swap'
            ? (action.removeKeys?.length ? action.removeKeys : [action.removeKey]).filter(Boolean)
            : [],
          action
        };
      } catch (error) {
        console.warn('[5.56 Starter] Insurance sync failed:', error);
        throw new Error('We could not finish preparing your order. Please try again.');
      }
    }

    syncInsuranceStateFromCart(cart) {
      const core = window.SubscriptionInsuranceCore;
      const config = core?.getInsuranceConfig?.();
      if (!config?.enabled) {
        this.cartMerchandiseSubtotalCents = 0;
        this.state.insurance = null;
        this.syncInsuranceDisplay();
        return;
      }

      this.cartMerchandiseSubtotalCents = core.getMerchandiseSubtotal?.(cart?.items || [], config) || 0;

      const insuranceLine = (cart?.items || []).find((line) => (
        core.isInsuranceVariantId(line.variant_id, config)
      ));
      const tier = config.tiers.find((candidate) => (
        String(candidate.variantId) === String(insuranceLine?.variant_id || '')
      ));
      if (!insuranceLine || !tier) {
        this.state.insurance = null;
        this.syncInsuranceDisplay();
        return;
      }

      const linePrice = Number(insuranceLine.final_line_price ?? insuranceLine.line_price);
      this.state.insurance = {
        variantId: Number(insuranceLine.variant_id),
        priceCents: Number.isFinite(linePrice) ? linePrice : Number(tier.price || 0),
        supportsSubscription: Boolean(tier.supportsSubscription),
        isSubscription: Boolean(insuranceLine.selling_plan_allocation),
        tier: tier.key || 'service tier'
      };
      this.syncInsuranceDisplay();
    }

    getProjectedInsurance(additionalMerchandiseCents) {
      const core = window.SubscriptionInsuranceCore;
      const config = core?.getInsuranceConfig?.();
      if (!config?.enabled || !core?.resolveTierForSubtotal) return null;

      const tier = core.resolveTierForSubtotal(
        Number(this.cartMerchandiseSubtotalCents || 0) + Number(additionalMerchandiseCents || 0),
        config
      );
      if (!tier) return null;
      return {
        variantId: Number(tier.variantId),
        priceCents: Number(tier.price || 0),
        supportsSubscription: Boolean(tier.supportsSubscription),
        isSubscription: Boolean(tier.supportsSubscription),
        tier: tier.key || 'service tier'
      };
    }

    syncInsuranceDisplay() {
      this.setText('[data-cart-starter-subtotal]', this.formatMoney(this.tripwirePriceCents));
      this.setText('[data-nmt-cart-count]', '1 item');
      this.updateReview();
    }

    verifyInsurance(cart, expectSubscription) {
      const core = window.SubscriptionInsuranceCore;
      const config = core?.getInsuranceConfig?.();
      if (!config?.enabled) return;
      if (!core?.inspectCartItems) {
        throw new Error('We could not verify your order. Please try again.');
      }

      const insuranceState = core.inspectCartItems(cart?.items || []);
      // Same rule as subscription-insurance-core: the premium tier ($1,000+) is opt-in and never auto-added, so no
      // insurance line is right there; a premium line the shopper added is fine too. Requiring a line here blocked
      // every $1,000+ cart ("We could not verify your order", Oct 29).
      const tier = insuranceState.expectedTier;
      if (tier && (tier.key === 'premium' || Number(tier.minSubtotal || 0) >= 100000)) {
        const items = insuranceState.insuranceItems || [];
        const optedIn = items.length === 1 && String(insuranceState.insuranceVariantId || '') === String(tier.variantId);
        if (items.length === 0 || optedIn) return;
        throw new Error('We could not verify your order. Please try again.');
      }
      if (!insuranceState.hasInsurance || insuranceState.insuranceItems?.length !== 1) {
        throw new Error('We could not verify your order. Please try again.');
      }
      if (
        insuranceState.expectedTier?.variantId &&
        String(insuranceState.insuranceVariantId || '') !== String(insuranceState.expectedTier.variantId)
      ) {
        throw new Error('We could not verify your order. Please try again.');
      }
      if (
        expectSubscription &&
        insuranceState.expectedTier?.supportsSubscription &&
        (
          insuranceState.insuranceType !== 'subscription' ||
          !insuranceState.insuranceFrequency ||
          Number(insuranceState.insuranceFrequency) > 30
        )
      ) {
        throw new Error('We could not verify your 30-day delivery. Please try again.');
      }
    }

    async getCart() {
      const root = window.Shopify?.routes?.root || '/';
      const fetcher = window.__nativeFetch || window.fetch;
      const response = await fetcher(`${root}cart.js`, {headers: {'Accept': 'application/json'}});
      if (!response.ok) throw new Error('We could not verify your cart. Please try again.');
      return response.json();
    }

    findTripwireLines(items) {
      return items.filter((line) =>
        Number(line.variant_id) === this.tripwireVariantId ||
        line.properties?._offer_key === this.offerKey && line.properties?._tripwire_item === 'true'
      );
    }

    // One starter per order. A second one in the cart (a quantity bump in the cart drawer, an older tab) used to stop
    // the pop-up with "We could not add your ammo"; now the cart goes back to one starter and the visitor carries on.
    // Returns true when the cart changed.
    async keepOneTripwire(lines) {
      if (lines.length < 2 && !lines.some((line) => Number(line.quantity) > 1)) return false;
      const updates = Object.fromEntries(lines.map((line, index) => [line.key, index === 0 ? 1 : 0]));
      const root = window.Shopify?.routes?.root || '/';
      const fetcher = window.__nativeFetch || window.fetch;
      const response = await fetcher(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({updates})
      });
      if (!response.ok) throw new Error(this.limitMessage);
      window.SubscriptionInsuranceCore?.clearCartCache?.();
      return true;
    }

    findClubLines(items) {
      return items.filter((line) =>
        line.properties?._offer_key === this.offerKey &&
        line.properties?._subscription_offer === this.clubOfferKey &&
        line.properties?._gift_item !== 'true'
      );
    }

    findGiftLines(items) {
      return items.filter((line) =>
        line.properties?._offer_key === this.offerKey &&
        line.properties?._subscription_offer === this.clubOfferKey &&
        line.properties?._gift_item === 'true'
      );
    }

    async removeLegacyClubDisplayProperties(lines) {
      const legacyPropertyNames = ['Club delivery', 'First order'];
      const root = window.Shopify?.routes?.root || '/';
      const fetcher = window.__nativeFetch || window.fetch;

      for (const line of lines) {
        const properties = {...(line.properties || {})};
        const hasLegacyProperties = legacyPropertyNames.some((name) => (
          Object.prototype.hasOwnProperty.call(properties, name)
        ));
        if (!hasLegacyProperties || !line.key) continue;

        legacyPropertyNames.forEach((name) => delete properties[name]);
        const response = await fetcher(`${root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: line.key,
            quantity: Number(line.quantity) || 1,
            properties
          })
        });

        if (!response.ok) {
          throw new Error('We could not refresh your monthly box. Please try again.');
        }
        window.SubscriptionInsuranceCore?.clearCartCache?.();
      }
    }

    // Runs after the new insurance line is added: keep that one line (keepItem), remove the rest by their current keys.
    // The keys read before the add go stale when a discount code is in the cart (#PA52681, Oct 19).
    async removeReplacedInsurance(keepItem) {
      const result = await window.SubscriptionInsuranceCore?.removeReplacedInsurance?.(keepItem);
      if (!result?.ok) {
        throw new Error('We could not finish preparing your order. Please try again.');
      }
    }

    async verifyGiftDiscount(addedItems) {
      let cart;
      try {
        cart = await this.getCart();
      } catch (error) {
        await this.removeAddedLines(addedItems);
        throw error;
      }

      const selectedCanId = this.state.can?.variantId;
      const gift = this.findGiftLines(cart.items || []).find((line) =>
        Number(line.variant_id) === selectedCanId
      );
      const finalLinePrice = Number(gift?.final_line_price ?? gift?.final_price);
      if (gift && Number.isFinite(finalLinePrice) && finalLinePrice === 0) return;

      await this.removeAddedLines(addedItems);
      throw new Error('We could not apply the free first-order gift, so your order was not sent to checkout. Please try again shortly.');
    }

    async addItems(items) {
      const root = window.Shopify?.routes?.root || '/';
      const fetcher = window.__nativeFetch || window.fetch;
      const response = await fetcher(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({items})
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'Could not add this offer to your cart.');
      }
      return response.json();
    }

    async removeAddedLines(addedItems) {
      const keys = (Array.isArray(addedItems) ? addedItems : addedItems?.items || [])
        .map((line) => line?.key)
        .filter(Boolean);
      if (keys.length === 0) return;

      const root = window.Shopify?.routes?.root || '/';
      const fetcher = window.__nativeFetch || window.fetch;
      const updates = Object.fromEntries(keys.map((key) => [key, 0]));
      const response = await fetcher(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({updates})
      });
      if (!response.ok) console.error('[5.56 Starter] Could not roll back an undiscounted offer cart.');
    }

    setBusy(isBusy) {
      this.state.busy = isBusy;
      this.querySelectorAll('[data-add-combined], [data-add-tripwire-only], [data-plan-option], [data-can-option], [data-gift-state]').forEach((element) => {
        element.classList.toggle('is-busy', isBusy);
      });
      const combinedButton = this.querySelector('[data-add-combined]');
      const starterButton = this.querySelector('[data-add-tripwire-only]');
      if (combinedButton) combinedButton.disabled = isBusy;
      if (starterButton) starterButton.disabled = isBusy;
      this.updateReview();
    }

    showError(message) {
      this.querySelectorAll('[data-error-message]').forEach((node) => {
        node.textContent = message || '';
        node.hidden = !message;
      });
    }

    showStateError() {
      const message = 'Choose your ship-to state before adding an engraved PMAG. We’ll automatically select the appropriate 10- or 30-round version.';
      const stateWrap = this.querySelector('[data-gift-state-wrap]');
      const stateSelect = this.querySelector('[data-gift-state]');
      const inlineError = this.querySelector('[data-gift-state-error]');
      if (stateWrap) {
        stateWrap.hidden = false;
        stateWrap.classList.add('is-invalid');
      }
      if (stateSelect) stateSelect.setAttribute('aria-invalid', 'true');
      if (inlineError) {
        inlineError.textContent = message;
        inlineError.hidden = false;
      }
      this.showError(message);
      stateWrap?.scrollIntoView?.({behavior: 'smooth', block: 'center'});
      window.setTimeout(() => stateSelect?.focus(), 180);
    }

    clearStateError() {
      const stateWrap = this.querySelector('[data-gift-state-wrap]');
      const stateSelect = this.querySelector('[data-gift-state]');
      const inlineError = this.querySelector('[data-gift-state-error]');
      stateWrap?.classList.remove('is-invalid');
      stateSelect?.removeAttribute('aria-invalid');
      if (inlineError) inlineError.hidden = true;
      this.showError('');
    }

    setCartConfirmation(success, message) {
      const cart = this.querySelector('[data-cart-confirmation]');
      if (!cart) return;
      cart.classList.toggle('is-error', !success);
      cart.querySelectorAll('[data-cart-success]').forEach((node) => {
        node.hidden = !success;
      });
      const error = cart.querySelector('[data-cart-error]');
      if (error) error.hidden = success;
      this.setText('[data-nmt-cart-count]', success ? '1 item' : 'Cart update');
      this.setText('[data-cart-error-text]', message);
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((node) => {
        node.textContent = value || '';
      });
    }

    getStateLabel() {
      if (!this.state.shipState) return '';
      const option = this.querySelector(`[data-gift-state] option[value="${this.state.shipState}"]`);
      return option?.textContent?.trim() || this.state.shipState;
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(undefined, {style: 'currency', currency}).format(Number(cents || 0) / 100);
      } catch (_error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    toPositiveInt(value, fallback = 0) {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    track(eventName, details = {}) {
      const payload = {
        event: `pa_556_tripwire_${eventName}`,
        offer_key: this.offerKey,
        ...details
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      this.dispatchEvent(new CustomEvent('pa:556-tripwire', {bubbles: true, detail: payload}));
    }
  });
})();
